import { PhotoIcon } from "@heroicons/react/24/solid";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { formatDuration } from "../../utility/bookingUtils";
import { formatPriceWithCurrency } from "../../utility/priceUtils";
import { COLOUR } from "../../config/constants";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import StatusIndicator from "../Shared/StatusIndicator";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { Fragment } from "react";

const BOOKABILITY_REASONS = {
  mobile: "Needs a mobile availability",
  fixed: "Needs a location availability",
  any: "Needs an availability",
};

// Amber attention pill for services whose delivery type has no matching
// availability. Mirrors the AppointmentTypeBadge xs idiom.
const BookabilityBadge = ({ bookability }) => {
  if (!bookability || bookability.bookable !== false) return null;
  const reason = BOOKABILITY_REASONS[bookability.missing] || BOOKABILITY_REASONS.any;
  return (
    <span
      title={reason}
      className="inline-flex items-center gap-x-1 rounded-pill bg-signal-amber-100 px-2 py-0.5 text-xs font-medium text-signal-amber-600"
    >
      <ExclamationTriangleIcon aria-hidden="true" className="size-3.5" />
      Not bookable yet
      <span className="sr-only"> — {reason}</span>
    </span>
  );
};

const ServiceDataTable = ({
  services,
  patchService,
  deleteService,
  editService,
  bookabilityById,
}) => {
  const { currency: userCurrency } = useUserPreferences();

  const handleActiveStatusClick = (service) => {
    const formData = new FormData();
    formData.append("isActive", !service.isActive);
    patchService({ id: service._id, formData });
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <p className="mt-2 text-sm text-ink-700">
            A list of all the services in your account including their name,
            description, price, and duration.
          </p>
        </div>
      </div>

      {/* Mobile view - Card layout */}
      <div className="mt-6 sm:hidden">
        <div className="space-y-4">
          {services?.map((service) => {
            const durationFormatted = formatDuration(service.duration, { compact: true });
            const priceFormatted = formatPriceWithCurrency(service.price, userCurrency);

            return (
              <div
                key={service._id}
                className="bg-white rounded-card border border-ink-100 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="size-12 shrink-0">
                      {service.imageUrl ? (
                        <img
                          alt=""
                          src={service.imageUrl}
                          className="size-12 rounded-card object-cover"
                        />
                      ) : (
                        <div
                          aria-hidden="true"
                          className="size-12 rounded-card bg-cream-100 border border-dashed border-ink-200 flex items-center justify-center"
                        >
                          <PhotoIcon className="size-6 text-ink-300" />
                        </div>
                      )}
                    </div>
                    <div className="ml-4 min-w-0 flex-1">
                      <div className="font-medium text-ink-900 truncate">
                        {service.name}
                      </div>
                      {bookabilityById?.[service._id]?.bookable === false && (
                        <div className="mt-1">
                          <BookabilityBadge
                            bookability={bookabilityById[service._id]}
                          />
                        </div>
                      )}
                      <div className="mt-1 text-sm text-ink-500 line-clamp-2">
                        {service.description}
                      </div>
                    </div>
                  </div>
                  <Menu as="div" className="relative flex-none ml-4">
                    <MenuButton className="-m-2.5 block p-2.5 text-ink-500 hover:text-ink-900">
                      <span className="sr-only">Open options</span>
                      <EllipsisVerticalIcon
                        aria-hidden="true"
                        className="size-5"
                      />
                    </MenuButton>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems anchor="bottom end" className="z-[9999] w-32 rounded-md bg-cream-50 py-2 shadow-lg ring-1 ring-ink-100 focus:outline-none">
                        <MenuItem>
                          {({ close }) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                editService(service._id);
                                close();
                              }}
                              className="block px-3 py-1 text-sm/6 text-ink-900 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                            >
                              Edit
                              <span className="sr-only">, {service.name}</span>
                            </a>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ close }) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleActiveStatusClick(service);
                                close();
                              }}
                              className="block px-3 py-1 text-sm/6 text-ink-900 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                            >
                              {service.isActive ? "Disable" : "Enable"}
                              <span className="sr-only">
                                , {service.isActive ? "Disable" : "Enable"}{" "}
                                {service.name}
                              </span>
                            </a>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ close }) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                deleteService(service._id);
                                close();
                              }}
                              className="block px-3 py-1 text-sm/6 text-signal-red-600 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                            >
                              Delete
                              <span className="sr-only">, {service.name}</span>
                            </a>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-center">
                    <span className="text-sm text-ink-500 w-16">Price:</span>
                    <span className="text-sm font-medium text-ink-900 ml-2">
                      {priceFormatted}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-ink-500 w-16">
                      Duration:
                    </span>
                    <span className="text-sm text-ink-900 ml-2">
                      {durationFormatted}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-ink-500 w-16">Status:</span>
                    <div className="ml-2">
                      <StatusIndicator
                        colour={service.isActive ? COLOUR.GREEN : COLOUR.GRAY}
                        label={service.isActive ? "Available" : "Unavailable"}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop view - Table layout */}
      <div className="mt-8 flow-root hidden sm:block">
        <div className="-mx-4 -my-2 overflow-visible sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-visible bg-white border border-ink-100 shadow-hairline sm:rounded-card">
              <table className="min-w-full">
                <thead className="border-b border-ink-100">
                  <tr>
                    <th
                      scope="col"
                      className="py-3 pl-4 pr-3 text-left smallcaps text-ink-400 sm:pl-6"
                    >
                      Service
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-right smallcaps text-ink-400"
                    >
                      Price
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-right smallcaps text-ink-400"
                    >
                      Duration
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left smallcaps text-ink-400"
                    >
                      Available
                    </th>
                    <th
                      scope="col"
                      className="relative py-3 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100 bg-white">
                  {services?.map((service) => {
                    const durationFormatted = formatDuration(service.duration, { compact: true });
                    const priceFormatted = formatPriceWithCurrency(service.price, userCurrency);

                    return (
                      <tr key={service._id} className="hover:bg-cream-100 transition-colors">
                        <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-ink-900 sm:w-auto sm:max-w-none sm:pl-6">
                          <div className="flex items-center">
                            <div className="size-11 shrink-0">
                              {service.imageUrl ? (
                                <img
                                  alt=""
                                  src={service.imageUrl}
                                  className="size-11 rounded-card object-cover"
                                />
                              ) : (
                                <div
                                  aria-hidden="true"
                                  className="size-11 rounded-card bg-cream-100 border border-dashed border-ink-200 flex items-center justify-center"
                                >
                                  <PhotoIcon className="size-5 text-ink-300" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="flex items-center gap-x-2">
                                <span className="font-medium text-ink-900">
                                  {service.name}
                                </span>
                                <BookabilityBadge
                                  bookability={bookabilityById?.[service._id]}
                                />
                              </div>
                              <div className="mt-1 text-ink-500 max-w-xs truncate">
                                {service.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-ink-900 font-medium tabular text-right">
                          {priceFormatted}
                        </td>
                        <td className="px-3 py-4 text-sm text-ink-500 tabular text-right">
                          {durationFormatted}
                        </td>
                        <td className="px-3 py-4 text-sm text-ink-500">
                          <StatusIndicator
                            colour={
                              service.isActive ? COLOUR.GREEN : COLOUR.GRAY
                            }
                            label={
                              service.isActive ? "Available" : "Unavailable"
                            }
                          />
                        </td>
                        <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <Menu as="div" className="relative flex-none">
                            <MenuButton className="-m-2.5 block p-2.5 text-ink-500 hover:text-ink-900">
                              <span className="sr-only">Open options</span>
                              <EllipsisVerticalIcon
                                aria-hidden="true"
                                className="size-5"
                              />
                            </MenuButton>

                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <MenuItems anchor="bottom end" className="z-[9999] w-32 rounded-md bg-cream-50 py-2 shadow-lg ring-1 ring-ink-100 focus:outline-none">
                                <MenuItem>
                                  {({ close }) => (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        editService(service._id);
                                        close();
                                      }}
                                      className="block px-3 py-1 text-sm/6 text-ink-900 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                    >
                                      Edit
                                      <span className="sr-only">
                                        , {service.name}
                                      </span>
                                    </a>
                                  )}
                                </MenuItem>
                                <MenuItem>
                                  {({ close }) => (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleActiveStatusClick(service);
                                        close();
                                      }}
                                      className="block px-3 py-1 text-sm/6 text-ink-900 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                    >
                                      {service.isActive ? "Disable" : "Enable"}
                                      <span className="sr-only">
                                        ,{" "}
                                        {service.isActive
                                          ? "Disable"
                                          : "Enable"}{" "}
                                        {service.name}
                                      </span>
                                    </a>
                                  )}
                                </MenuItem>
                                <MenuItem>
                                  {({ close }) => (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        deleteService(service._id);
                                        close();
                                      }}
                                      className="block px-3 py-1 text-sm/6 text-signal-red-600 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                    >
                                      Delete
                                      <span className="sr-only">
                                        , {service.name}
                                      </span>
                                    </a>
                                  )}
                                </MenuItem>
                              </MenuItems>
                            </Transition>
                          </Menu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDataTable;
